import { Component } from 'preact';
import { UI } from '../utils/arena/uiSounds';
import AppHeader from './AppHeader';
import { BackButton } from './ui/navButtons';
import AmbientParticles from './AmbientParticles';
import StoreTorchFX from './StoreTorchFX';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  VIGNETTE, BEVELED_BTN, GOLD_BTN,
} from '../lib/medievalTheme';
import { getMissingCards, effectiveDeckMode } from '../utils/sorcery/deckRules';
import { isFreePlay } from '../utils/freePlay';
import { timeAgo } from '../utils/arena/timeFormat';
import DeckTile, { DeckTileText, DeckModePill, TXT } from './DeckTile';
import { isPhoneLayout } from '../utils/platform';
import DeckAvatarFilter from './DeckAvatarFilter';
import { t } from '../localization/t';

function getAvatarFromDeck(deck, sorceryCards) {
  if (!deck?.cards || !sorceryCards) return null;
  for (const entry of deck.cards) {
    const card = sorceryCards.find((c) => c.unique_id === entry.cardId);
    if (card?.type === 'Avatar') return card;
  }
  return null;
}

function getAvatarImageUrl(card) {
  return card?.printings?.[0]?.image_url || null;
}

// Art for the selected deck's avatar, following the printing the deck itself
// uses so an alt-art avatar shows the player's own version. The filter
// dropdown deliberately stays on the base art: it groups decks by avatar
// CARD, and two decks can hold two different arts of the same avatar.
function getDeckAvatarImageUrl(deck, sorceryCards) {
  if (!deck?.cards || !sorceryCards) return null;
  for (const entry of deck.cards) {
    const card = sorceryCards.find((c) => c.unique_id === entry.cardId);
    if (card?.type !== 'Avatar') continue;
    const printing = card.printings?.find((p) => p.unique_id === entry.printingId)
      || card.printings?.[0];
    return printing?.image_url || null;
  }
  return null;
}

const SECTION_LABEL_STYLE = {
  color: `${GOLD} 0.55)`,
 fontSize: '0.625cqw',
 fontWeight: 700,
 fontFamily: "'FantaisieArtistique', cursive",
};

export default class ArenaDeckSelect extends Component {
 constructor(props) {
 super(props);
 this.state = {
 selected: null,
 searchQuery: '',
 avatarFilter: null,
 sort: 'latest',
 // Avatar + sort live as dropdowns integrated into the search bar
 // The same toolbar pattern the deck gallery uses, so players
 // see one affordance shape across both surfaces.
 showAvatarMenu: false,
 showSortMenu: false,
 };
 }

 getAvatarOptions() {
 const { decks, sorceryCards } = this.props;
 const byId = new Map();
 for (const deck of decks) {
 const avatar = getAvatarFromDeck(deck, sorceryCards);
 if (avatar && !byId.has(avatar.unique_id)) {
 byId.set(avatar.unique_id, {
 value: avatar.unique_id,
 label: avatar.name,
 imageUrl: getAvatarImageUrl(avatar),
 });
 }
 }
 return Array.from(byId.values()).sort((a, b) => a.label.localeCompare(b.label));
 }

 getFilteredDecks() {
 const { decks, sorceryCards } = this.props;
 const { searchQuery, avatarFilter, sort } = this.state;
 const q = searchQuery.toLowerCase().trim();

 const filtered = decks.filter((deck) => {
 if (q && !deck.name.toLowerCase().includes(q)) return false;
 if (avatarFilter) {
 const avatar = getAvatarFromDeck(deck, sorceryCards);
 if (!avatar || avatar.unique_id !== avatarFilter) return false;
 }
 return true;
 });

 // Same ordering the gallery offers. 'latest' is the incoming order,
 // so only the other two need work.
 if (sort === 'name') filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
 else if (sort === 'oldest') filtered.sort((a, b) => new Date(a.savedAt || 0) - new Date(b.savedAt || 0));
 return filtered;
 }

 renderSectionLabel(text) {
 return (
 <div className="flex items-center gap-2 mb-2">
 <div className="h-px w-3" style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.55) 100%)` }} />
        <span style={SECTION_LABEL_STYLE}>{text}</span>
        <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${GOLD} 0.55) 0%, transparent 100%)` }} />
      </div>
    );
  }

  render() {
    const {
      decks, sorceryCards, ownedMap, onConfirm, onCancel, pickerMode = 'ranked', format,
      titleOverride, subtitleOverride,
    } = this.props;
    const { selected, searchQuery, avatarFilter } = this.state;
    // Free play has no Sandbox to name: every deck plays everywhere.
    const isSandbox = pickerMode === 'sandbox' && !isFreePlay();
    const isPeasant = format === 'peasant';

    const avatarOptions = this.getAvatarOptions();
    const filtered = this.getFilteredDecks();
    const selectedDeck = selected ? decks.find((d) => d.id === selected) : null;
    const selectedAvatar = selectedDeck ? getAvatarFromDeck(selectedDeck, sorceryCards) : null;
    const selectedAvatarUrl = selectedDeck ? getDeckAvatarImageUrl(selectedDeck, sorceryCards) : null;

    const title = titleOverride || (isSandbox
      ? t('Choose Your Sandbox Deck')
      : isPeasant
        ? t('Choose Your Peasant Deck')
        : t('Choose Your Deck'));
    const subtitle = subtitleOverride || (isSandbox
      ? t('Any Deck. No Rewards.')
      : isPeasant
        ? t('Ordinary and Exceptional cards only.')
        : t('Select a deck for ranked play.'));

    return (
      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{ background: 'url("/deck-builder-bg.webp") center no-repeat, #08080a', backgroundSize: '100% 100%' }}
      >
        <div className="absolute inset-0 pointer-events-none" style={{ background: VIGNETTE }} />
        <StoreTorchFX />
        <AmbientParticles preset="deckbuilder" />

        {isPhoneLayout() ? (
          /* Compact one-line bar: the desktop AppHeader is cqw-sized
             chrome. Safe-area insets keep the bar off the notch. */
          <div
            className="relative z-20 shrink-0 flex items-baseline gap-2 pt-2 pb-1"
            style={{
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            }}
          >
            <h1 className="arena-heading text-lg font-bold truncate" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{title}</h1>
            <span className="truncate" style={{ fontSize: '0.6875rem', color: `${GOLD} 0.5)`, fontFamily: "'FantaisieArtistique', cursive" }}>
              {subtitle}
            </span>
          </div>
        ) : (
          <AppHeader
            profile={this.props.profile}
            onToggleMailbox={this.props.onToggleMailbox}
            mailboxUnreadCount={this.props.mailboxUnreadCount}
            mailboxOpen={this.props.mailboxOpen}
          >
            <BackButton title={t('Cancel')} onClick={onCancel} />
            <div className="min-w-0">
              <h1 className="arena-heading text-lg font-bold truncate" style={{ color: TEXT_PRIMARY, textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}>{title}</h1>
              <div className="text-[0.6875cqw] truncate" style={{ color: `${GOLD} 0.5)`, fontFamily: "'FantaisieArtistique', cursive" }}>
                {subtitle}
              </div>
            </div>
          </AppHeader>
        )}

        {/* Toolbar. Same row shape as the deck gallery's: a search
            capsule with the avatar filter built into its right edge,
            deck count pushed to the far right. */}
        {decks.length > 0 ? (
          <div
            className={`deck-select-rise relative z-20 shrink-0 pt-2 pb-2 flex items-center gap-3 ${isPhoneLayout() ? '' : 'px-6 pt-3 pb-3'}`}
            style={{
              overflow: 'visible',
              '--rise-delay': '40ms',
              '--rise-from': '0.5cqw',
              ...(isPhoneLayout() ? {
                paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
                paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
              } : {}),
            }}
          >
            {isPhoneLayout() ? (() => {
              const pill = (active, label, onClick) => (
                <button
                  key={label}
                  type="button"
                  className="px-3 rounded-full text-xs font-semibold transition-all"
                  style={{
                    minHeight: '2rem',
                    background: active ? `${GOLD} 0.16)` : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${GOLD} ${active ? '0.55)' : '0.16)'}`,
                    color: active ? ACCENT_GOLD : TEXT_BODY,
                  }}
                  onClick={onClick}
                >
                  {label}
                </button>
              );
              const anyFilter = avatarFilter || this.state.sort !== 'latest';
              return (
                <>
                  <div className="flex items-center flex-1 min-w-0 rounded-lg" style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)` }}>
                    <div className="px-2.5 shrink-0" style={{ color: TEXT_MUTED }}>
                      <svg style={{ width: '1rem', height: '1rem' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder={t('Search decks...')}
                      value={searchQuery}
                      onInput={(e) => this.setState({ searchQuery: e.target.value })}
                      className="flex-1 min-w-0 px-1 py-2 text-sm outline-none"
                      style={{ background: 'transparent', color: TEXT_BODY, border: 'none' }}
                    />
                  </div>
                  <div className="relative shrink-0">
                    <button
                      type="button"
                      aria-label={t('Filters')}
                      className="relative flex items-center justify-center rounded-lg transition-all active:scale-[0.95]"
                      style={{
                        width: '2.75rem',
                        height: '2.75rem',
                        background: '#0e0b08',
                        border: `1px solid ${GOLD} ${this.state.showMobileFilters || anyFilter ? '0.5)' : '0.18)'}`,
                        color: anyFilter ? ACCENT_GOLD : TEXT_MUTED,
                      }}
                      onClick={() => this.setState((st) => ({ showMobileFilters: !st.showMobileFilters }))}
                    >
                      <svg style={{ width: '1.25rem', height: '1.25rem' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                      </svg>
                      {anyFilter ? (
                        <span className="absolute rounded-full" style={{ top: '0.375rem', right: '0.375rem', width: '0.5rem', height: '0.5rem', background: ACCENT_GOLD }} />
                      ) : null}
                    </button>
                    {this.state.showMobileFilters ? (
                      <div
                        className="absolute right-0 top-full mt-1 z-50 rounded-xl flex flex-col gap-3 p-3 overflow-y-auto"
                        style={{
                          width: 'min(22rem, calc(100vw - 2rem))',
                          maxHeight: '14rem',
                          background: '#0e0b08',
                          border: `1px solid ${GOLD} 0.25)`,
                          boxShadow: '0 12px 32px rgba(0,0,0,0.75)',
                        }}
                      >
                        <div>
                          <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Sort')}</div>
                          <div className="flex flex-wrap gap-1.5">
                            {[['latest', t('Latest')], ['name', t('Name')], ['oldest', t('Oldest')]].map(([key, label]) => pill(
                              this.state.sort === key, label, () => this.setState({ sort: key }),
                            ))}
                          </div>
                        </div>
                        {avatarOptions.length > 1 ? (
                          <div>
                            <div className="text-xs font-semibold mb-1.5" style={{ color: `${GOLD} 0.55)`, fontFamily: "'FantaisieArtistique', cursive" }}>{t('Avatar')}</div>
                            <div className="flex flex-wrap gap-1.5">
                              {pill(!avatarFilter, t('Any Avatar'), () => this.setState({ avatarFilter: null }))}
                              {avatarOptions.map((o) => pill(
                                avatarFilter === o.value, o.label, () => this.setState({ avatarFilter: o.value }),
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <button
                          type="button"
                          className="self-end px-4 rounded-lg text-xs font-semibold"
                          style={{ minHeight: '2.25rem', background: `${GOLD} 0.14)`, border: `1px solid ${GOLD} 0.4)`, color: ACCENT_GOLD }}
                          onClick={() => this.setState({ showMobileFilters: false })}
                        >
                          {t('Done')}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </>
              );
            })() : (
            <div
              className="flex items-center flex-1 rounded-lg overflow-visible max-w-[36cqw]"
              style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.18)` }}
            >
              <div className="px-2.5 shrink-0" style={{ color: TEXT_MUTED }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.8125cqw', height: '0.8125cqw' }}>
                  <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
              <div className="w-px h-5 shrink-0" style={{ background: `${GOLD} 0.12)` }} />
              <input
                type="text"
                placeholder={t('Search decks...')}
                value={searchQuery}
                onInput={(e) => this.setState({ searchQuery: e.target.value })}
                className="flex-1 min-w-0 px-2.5 py-2 text-xs outline-none"
                style={{ background: 'transparent', color: TEXT_BODY, border: 'none' }}
              />
              {avatarOptions.length > 1 ? (
                <DeckAvatarFilter
                  options={avatarOptions}
                  value={avatarFilter}
                  open={this.state.showAvatarMenu}
                  onToggle={() => this.setState((s) => ({ showAvatarMenu: !s.showAvatarMenu, showSortMenu: false }))}
                  onChange={(v) => this.setState({ avatarFilter: v, showAvatarMenu: false })}
                />
              ) : null}
              {/* Sort. Same options and order as the gallery's. */}
              <div className="relative shrink-0" style={{ borderLeft: `1px solid ${GOLD} 0.12)` }}>
                <button
                  type="button"
                  className="flex items-center gap-2 px-3 py-2 text-xs transition-all"
                  style={{ color: TEXT_PRIMARY }}
                  onClick={() => this.setState((s) => ({ showSortMenu: !s.showSortMenu, showAvatarMenu: false }))}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.875cqw', height: '0.875cqw' }}>
                    <path d="M7 3v18M3 7l4-4 4 4M17 21V3M21 17l-4 4-4-4" />
                  </svg>
                  {{ latest: t('Latest'), name: t('Name'), oldest: t('Oldest') }[this.state.sort]}
                </button>
                {this.state.showSortMenu ? (
                  <div
                    className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden"
                    style={{ background: '#0e0b08', border: `1px solid ${GOLD} 0.2)`, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', minWidth: '8.75cqw' }}
                  >
                    {[{ key: 'latest', label: t('Latest') }, { key: 'name', label: t('Name') }, { key: 'oldest', label: t('Oldest') }].map((o) => (
                      <button
                        key={o.key}
                        type="button"
                        className="w-full px-4 py-2.5 text-xs text-left transition-all"
                        style={{ color: this.state.sort === o.key ? ACCENT_GOLD : TEXT_BODY }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = `${GOLD} 0.08)`; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                        onClick={() => this.setState({ sort: o.key, showSortMenu: false })}
                      >
                        {o.label}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
            )}
            <span className="text-xs ml-auto shrink-0" style={{ color: TEXT_MUTED }}>
              {filtered.length === 1 ? t('1 deck') : t('{n} decks', { n: filtered.length })}
            </span>
          </div>
        ) : null}

        {/* Deck grid. Identical track sizing to the deck gallery, so a
            full-width canvas lays out four 22cqw tiles per row. */}
        <div
          className={`deck-select-rise relative flex-1 min-h-0 overflow-y-auto medieval-scrollbar py-3 ${isPhoneLayout() ? '' : 'px-6 py-4'}`}
          style={{
            '--rise-delay': '90ms',
            '--rise-from': '0.9cqw',
            ...(isPhoneLayout() ? {
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            } : {}),
          }}
        >
          {decks.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center px-8">
              <div>
                <div className="text-sm mb-2" style={{ color: TEXT_BODY, fontFamily: "'FantaisieArtistique', cursive" }}>{t('No decks yet')}</div>
                <div className="text-[0.75cqw]" style={{ color: TEXT_MUTED }}>{t('Build one in the Deck Builder to start playing matches.')}</div>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="h-full flex items-center justify-center text-center">
              <div className="text-sm italic" style={{ color: TEXT_MUTED }}>{t('No decks match your search.')}</div>
            </div>
          ) : (
            <div className={isPhoneLayout() ? 'grid gap-3' : 'grid gap-5'} style={{ gridTemplateColumns: isPhoneLayout() ? 'repeat(3, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(22cqw, 1fr))' }}>
              {filtered.map((deck) => {
                const isSelected = selected === deck.id;
                const { count: missingCount } = getMissingCards(deck.cards || [], ownedMap);
                const deckMode = effectiveDeckMode(deck.gameMode);
                const isSandboxDeck = deckMode === 'sandbox';

                return (
                  <DeckTile
                    key={deck.id}
                    deck={deck}
                    sorceryCards={sorceryCards}
                    selected={isSelected}
                    onClick={() => this.setState({ selected: deck.id })}
                    topRightSlot={isPhoneLayout() && !isSelected ? <DeckModePill mode={deckMode} /> : isSelected ? (
                      <div className="flex items-center justify-center rounded-full" style={{ width: '1.5cqw', height: '1.5cqw', background: ACCENT_GOLD, boxShadow: `0 0 0.6cqw ${GOLD} 0.6)` }}>
                        <svg viewBox="0 0 14 14" fill="none" style={{ width: '0.85cqw', height: '0.85cqw' }}>
                          <path d="M3.5 7l2.5 2.5 4.5-5" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    ) : null}
                    footerLeftSlot={(
                      <>
                        {DeckTileText.title(deck.name)}
                        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                          {deck.savedAt ? <span style={{ color: TEXT_MUTED, fontSize: TXT(0.58, 0.75) }}>{timeAgo(deck.savedAt)}</span> : null}
                          {isPhoneLayout() ? null : <DeckModePill mode={deckMode} />}
                        </div>
                      </>
                    )}
                    footerRightSlot={(
                      <>
                        {missingCount > 0 && !isSandboxDeck ? (
                          <span className="px-1.5 py-0.5 rounded-full" style={{ fontSize: TXT(0.5, 0.625), background: 'rgba(180,60,60,0.2)', color: '#e89090', border: '1px solid rgba(180,60,60,0.3)' }}>
                            {t('{n} missing', { n: missingCount })}
                          </span>
                        ) : null}
                        <span style={{ color: TEXT_MUTED, fontSize: TXT(0.6, 0.75) }}>{t('{n} cards', { n: deck.cardCount ?? deck.cards?.length ?? 0 })}</span>
                      </>
                    )}
                  />
                );
              })}
            </div>
          )}
        </div>

        {/* Action bar. The gallery has no equivalent because browsing
            isn't a commit step. Mirrors the header's recessed strip so
            the view reads as bracketed top and bottom. Cancel lives in
            the header's back button, not here. */}
        <div
          className={`deck-select-rise relative z-20 flex items-center gap-4 shrink-0 ${isPhoneLayout() ? 'pt-2' : 'px-6 py-3'}`}
          style={{
            background: 'rgba(12, 10, 8, 0.92)',
            backdropFilter: 'blur(8px)',
            borderTop: `1px solid ${GOLD} 0.15)`,
            '--rise-delay': '130ms',
            '--rise-from': '0.75cqw',
            ...(isPhoneLayout() ? {
              paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
              paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
              paddingBottom: 'max(0.5rem, env(safe-area-inset-bottom, 0px))',
            } : {}),
          }}
        >
          {/* Phone: the desktop Cancel lives in the AppHeader back
              button, which the compact bar drops. */}
          {isPhoneLayout() ? (
            <button
              type="button"
              className="px-4 text-sm shrink-0 transition-all duration-150 active:scale-[0.98]"
              style={{ ...BEVELED_BTN, minHeight: '2.5rem', borderRadius: 'var(--radius-md)', fontFamily: "'FantaisieArtistique', cursive", color: TEXT_BODY }}
              onClick={onCancel}
            >
              {t('Cancel')}
            </button>
          ) : null}
          <div className="flex-1 min-w-0">
            {selectedDeck ? (
              <div className="flex items-center gap-3">
                {selectedAvatarUrl ? (
                  <img src={selectedAvatarUrl} alt="" className="w-10 h-10 rounded-lg object-cover object-top" style={{ border: `1px solid ${GOLD} 0.4)` }} />
                ) : null}
                <div className="min-w-0">
                  <div className="text-sm font-bold truncate" style={{ color: TEXT_PRIMARY, fontFamily: "'FantaisieArtistique', cursive" }}>{selectedDeck.name}</div>
                  <div className="text-[0.625cqw] mt-0.5" style={{ color: `${GOLD} 0.55)` }}>
                    {t('{n} cards', { n: selectedDeck.cards?.length || 0 })}{selectedAvatar ? ` · ${selectedAvatar.name}` : ''}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm italic" style={{ color: TEXT_MUTED }}>{t('No deck selected')}</div>
            )}
          </div>

          {decks.length > 0 ? (
            <button
              type="button"
              disabled={!selected}
              data-sound={UI.CONFIRM}
              className="px-7 py-2 text-sm font-semibold transition-all duration-150 hover:scale-[1.03] active:scale-[0.98] disabled:opacity-30 disabled:hover:scale-100"
              style={selected
                ? { ...GOLD_BTN, borderRadius: 'var(--radius-md)', fontFamily: "'FantaisieArtistique', cursive", letterSpacing: '0.06em' }
                : { ...BEVELED_BTN, color: TEXT_MUTED, borderRadius: 'var(--radius-md)', fontFamily: "'FantaisieArtistique', cursive", letterSpacing: '0.06em' }
              }
              onClick={() => onConfirm(selected)}
            >
              {t('Find Match')}
            </button>
          ) : null}
        </div>
      </div>
    );
  }
}
